import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, MapPin, Pencil, CalendarDays, Copy, X } from "lucide-react";
import { getPropertyType } from "@/lib/propertyTypes";
import {
  DETAIL_SCHEMAS,
  BUILDING_FIELDS,
  VARIANT_STYLES,
  isMoneyField,
  isAreaField,
  formatMoney,
  formatArea,
  unitPrice,
  formatUnitPrice,
  formatDate,
  effectiveTotals,
} from "@/lib/detailSchemas";

const VALUE_ROWS_TYPES = ["mesken", "ofis", "is_yeri", "arsa", "tarla", "bahce"];
const ARSA_BINA_TYPES = ["arsa_bina", "fabrika"];

const fmtValue = (key, value) => {
  if (value === "" || value === null || value === undefined) return "—";
  if (isMoneyField(key)) return formatMoney(value);
  if (isAreaField(key)) return formatArea(value);
  return String(value);
};

export default function ExpertiseDetailPanel({ item, onClose, onDelete, onEdit, onDuplicate }) {
  if (!item) return null;
  const type = getPropertyType(item.property_type);
  const schema = DETAIL_SCHEMAS[item.property_type];
  const details = item.details || {};
  const useValueRowsView = VALUE_ROWS_TYPES.includes(item.property_type);
  const useArsaBinaView = ARSA_BINA_TYPES.includes(item.property_type);
  const up = unitPrice(item);

  const arsaDegeri = Number(details.arsa_degeri) || 0;
  const yasalBinaToplam = Number(details.yasal_bina_toplam) || 0;
  const mevcutBinaToplam = Number(details.mevcut_bina_toplam) || 0;
  const yasalToplam = arsaDegeri + yasalBinaToplam;
  const mevcutToplam = arsaDegeri + mevcutBinaToplam;

  return (
    <div
      className="flex-1 min-h-0 flex flex-col bg-white"
      data-testid="detail-panel"
    >
      <div className="px-5 pt-4 pb-3 border-b border-zinc-200 space-y-2.5 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: type.color }}
            />
            <Badge
              variant="outline"
              className="rounded-sm px-2 py-0.5 text-[11px] font-medium border-zinc-300"
              style={{ color: type.color, borderColor: `${type.color}66` }}
            >
              {type.label}
            </Badge>
            <span className="text-xs text-zinc-400 font-mono">
              Ada {item.ada} · Parsel {item.parsel}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-900 -mr-1 -mt-0.5"
            aria-label="Kapat"
            data-testid="detail-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="text-xl font-bold tracking-tight leading-snug text-zinc-900">
          {item.neighborhood_name} Mahallesi
        </div>
        <div className="flex items-center gap-1.5 text-xs text-zinc-500">
          <MapPin className="h-3.5 w-3.5" />
          <span>
            {item.district_name} / {item.province_name}
          </span>
        </div>
        {item.expertise_date && (
          <div
            className="inline-flex items-center gap-1.5 text-[11px] font-mono text-zinc-700 bg-zinc-100 border border-zinc-200 rounded-sm px-2 py-1 self-start"
            data-testid="detail-expertise-date"
          >
            <CalendarDays className="h-3 w-3 text-zinc-500" />
            <span>Ekspertiz: {formatDate(item.expertise_date)}</span>
          </div>
        )}
        {up && (
          <div
            className="rounded-sm border border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100 px-3 py-2 flex items-baseline justify-between"
            data-testid="detail-unit-price-card"
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800">
              Birim Değer
            </div>
            <div className="text-lg font-mono font-bold text-amber-900 tracking-tight">
              {formatUnitPrice(up)}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
        {useValueRowsView && <MOValueView details={details} propertyType={item.property_type} />}
        {useArsaBinaView && (
          <ArsaBinaValueView
            arsaDegeri={arsaDegeri}
            yasalBinaToplam={yasalBinaToplam}
            mevcutBinaToplam={mevcutBinaToplam}
            yasalToplam={yasalToplam}
            mevcutToplam={mevcutToplam}
            details={details}
          />
        )}
        {schema?.sections?.map((section, idx) => {
          const style = VARIANT_STYLES[section.variant] || VARIANT_STYLES.default;
          if ((useValueRowsView || useArsaBinaView) && (section.variant === "area" || section.variant === "value")) return null;
          return (
              <section
                key={idx}
                className={`rounded-sm border ${style.card} p-3.5`}
                data-testid={`sheet-section-${section.variant}`}
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <span className={`h-3 w-1 rounded-sm ${style.accent}`} />
                  <div className={`text-[11px] uppercase tracking-widest font-bold ${style.header}`}>
                    {section.title}
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {section.fields.map((f) => (
                    <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
                      <dt className="text-[10px] text-zinc-500">{f.label}</dt>
                      <dd className="text-sm text-zinc-900 font-semibold mt-0.5">
                        {fmtValue(f.key, details[f.key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            );
          })}

          {schema?.buildings && (details.buildings || []).length > 0 && (
            <section className="space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                Binalar
              </div>
              <div className="space-y-3">
                {details.buildings.map((b, idx) => (
                  <div
                    key={idx}
                    className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/50"
                    data-testid={`building-view-${idx}`}
                  >
                    <div className="text-sm font-semibold text-zinc-900 mb-2">
                      {b.bina_adi || `Bina #${idx + 1}`}
                    </div>
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                      {BUILDING_FIELDS.filter((f) => f.key !== "bina_adi").map((f) => (
                        <div key={f.key}>
                          <dt className="text-zinc-500">{f.label}</dt>
                          <dd className="text-zinc-900 font-medium">
                            {fmtValue(f.key, b[f.key])}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            </section>
          )}

          {item.notes && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-2">
                Notlar
              </div>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">
                {item.notes}
              </p>
            </section>
          )}

          <section>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mb-2">
              Konum
            </div>
            <div className="text-xs font-mono text-zinc-600">
              {item.latitude.toFixed(5)}, {item.longitude.toFixed(5)}
            </div>
          </section>
      </div>

      <div className="border-t border-zinc-200 px-5 py-3 shrink-0 bg-white flex items-center gap-2">
        <Button
          type="button"
          variant="default"
          onClick={() => onEdit && onEdit(item)}
          className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-10"
          data-testid="detail-edit-btn"
        >
          <Pencil className="h-4 w-4 mr-2" />
          Güncelle
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => onDuplicate && onDuplicate(item)}
          className="rounded-sm px-3 h-10"
          data-testid="detail-duplicate-btn"
          aria-label="Bu kayıttan çoğalt"
          title="Bu kayıttan çoğalt"
        >
          <Copy className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => onDelete && onDelete(item.id)}
          className="rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 px-3 h-10"
          data-testid="detail-delete-btn"
          aria-label="Kaydı sil"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// Compact key metric extractor used in list items
export function summaryLine(item) {
  const d = item.details || {};
  const type = item.property_type;
  const up = unitPrice(item);
  const upStr = up ? formatUnitPrice(up) : "";

  if (type === "arsa") {
    const parts = [];
    if (d.yasal_alan) parts.push(formatArea(d.yasal_alan));
    if (d.mevcut_deger) parts.push(formatMoney(d.mevcut_deger));
    else if (d.yasal_deger) parts.push(formatMoney(d.yasal_deger));
    if (upStr) parts.push(upStr);
    return parts.join(" · ");
  }
  if (type === "tarla" || type === "bahce") {
    const parts = [];
    if (d.yasal_alan) parts.push(formatArea(d.yasal_alan));
    if (d.mevcut_deger) parts.push(formatMoney(d.mevcut_deger));
    else if (d.yasal_deger) parts.push(formatMoney(d.yasal_deger));
    if (upStr) parts.push(upStr);
    return parts.join(" · ");
  }
  if (type === "mesken" || type === "ofis" || type === "is_yeri") {
    const parts = [];
    if (d.mevcut_alan) parts.push(formatArea(d.mevcut_alan));
    if (d.blok) parts.push(`Blok ${d.blok}`);
    if (d.kat) parts.push(`Kat ${d.kat}`);
    if (d.mevcut_deger) parts.push(formatMoney(d.mevcut_deger));
    if (upStr) parts.push(upStr);
    return parts.join(" · ");
  }
  if (type === "arsa_bina" || type === "fabrika") {
    const arsa = Number(d.arsa_degeri) || 0;
    const yasalBina = Number(d.yasal_bina_toplam) || 0;
    const mevcutBina = Number(d.mevcut_bina_toplam) || 0;
    const totalPreferred = mevcutBina > 0 ? arsa + mevcutBina : arsa + yasalBina;
    const parts = [];
    if (d.yasal_alan) parts.push(formatArea(d.yasal_alan));
    if (totalPreferred > 0) parts.push(formatMoney(totalPreferred));
    if (upStr) parts.push(upStr);
    return parts.join(" · ");
  }
  return "";
}


function MOValueBlock({ label, prefix, details, variant }) {
  const style = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  const floors = Array.isArray(details?.[`${prefix}_floors`]) ? details[`${prefix}_floors`] : [];
  const hasFloors = floors.length > 0;
  const { alan, deger } = effectiveTotals(details, prefix);
  const unit = alan > 0 && deger > 0 ? Math.round(deger / alan) : null;
  return (
    <section
      className={`rounded-sm border ${style.card} p-3.5`}
      data-testid={`sheet-mo-${prefix}`}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <span className={`h-3 w-1 rounded-sm ${style.accent}`} />
        <div className={`text-[11px] uppercase tracking-widest font-bold ${style.header}`}>
          {label}
        </div>
      </div>
      {hasFloors ? (
        <div className="space-y-1">
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 px-1 text-[9px] uppercase tracking-widest font-bold text-zinc-500">
            <span>Kat</span>
            <span className="text-right">Alan</span>
            <span className="text-right">Birim</span>
            <span className="text-right">Toplam</span>
          </div>
          {floors.map((f, i) => {
            const a = Number(f.alan) || 0;
            const d = Number(f.deger) || 0;
            const u = a > 0 && d > 0 ? Math.round(d / a) : null;
            return (
              <div
                key={i}
                className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 px-1 py-1 text-xs bg-white/70 rounded-sm"
                data-testid={`sheet-${prefix}-floor-${i}`}
              >
                <span className="text-zinc-800 font-medium truncate">{f.kat || "—"}</span>
                <span className="text-right font-mono text-zinc-700">
                  {a > 0 ? formatArea(a) : "—"}
                </span>
                <span className="text-right font-mono text-zinc-500">
                  {u ? `${u.toLocaleString("tr-TR")} ₺/m²` : "—"}
                </span>
                <span className="text-right font-mono font-semibold text-zinc-900">
                  {d > 0 ? formatMoney(d) : "—"}
                </span>
              </div>
            );
          })}
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 px-1 pt-2 mt-1 border-t border-zinc-200 text-xs">
            <span className="text-zinc-500 uppercase tracking-widest text-[10px] font-bold">
              Toplam
            </span>
            <span className="text-right font-mono font-semibold">
              {alan > 0 ? formatArea(alan) : "—"}
            </span>
            <span className="text-right font-mono text-zinc-500">
              {unit ? `${unit.toLocaleString("tr-TR")} ₺/m²` : "—"}
            </span>
            <span className="text-right font-mono font-bold text-zinc-900">
              {deger > 0 ? formatMoney(deger) : "—"}
            </span>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-3 gap-x-3 gap-y-2">
          <div>
            <dt className="text-[10px] text-zinc-500">Alan</dt>
            <dd className="text-sm text-zinc-900 font-semibold mt-0.5">
              {alan > 0 ? formatArea(alan) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-zinc-500">Birim</dt>
            <dd className="text-sm text-zinc-700 font-mono mt-0.5">
              {unit ? `${unit.toLocaleString("tr-TR")} ₺/m²` : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-zinc-500">Toplam</dt>
            <dd className="text-sm text-zinc-900 font-bold mt-0.5">
              {deger > 0 ? formatMoney(deger) : "—"}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}

function MOValueView({ details }) {
  return (
    <div className="space-y-3">
      <MOValueBlock label="Yasal Değer" prefix="yasal" details={details} variant="area" />
      <MOValueBlock label="Mevcut Değer" prefix="mevcut" details={details} variant="value" />
    </div>
  );
}


function ArsaBinaValueView({
  arsaDegeri,
  yasalBinaToplam,
  mevcutBinaToplam,
  yasalToplam,
  mevcutToplam,
  details,
}) {
  const alan = Number(details?.yasal_alan) || 0;
  const unit = alan > 0 && arsaDegeri > 0 ? Math.round(arsaDegeri / alan) : null;
  return (
    <div className="space-y-3">
      <section
        className="rounded-sm border border-emerald-200 bg-emerald-50/50 p-3.5"
        data-testid="detail-view-arsa"
      >
        <div className="flex items-center gap-2 mb-2.5">
          <span className="h-3 w-1 rounded-sm bg-emerald-500" />
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-800">
            Arsa Değeri
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-x-3 gap-y-2">
          <div>
            <dt className="text-[10px] text-zinc-500">Alan</dt>
            <dd className="text-sm font-semibold text-zinc-900 mt-0.5">
              {alan > 0 ? formatArea(alan) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-zinc-500">Birim</dt>
            <dd className="text-sm font-mono text-zinc-700 mt-0.5">
              {unit ? `${unit.toLocaleString("tr-TR")} ₺/m²` : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] text-zinc-500">Toplam</dt>
            <dd className="text-sm font-bold text-zinc-900 mt-0.5">
              {arsaDegeri > 0 ? formatMoney(arsaDegeri) : "—"}
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="rounded-sm border border-sky-200 bg-sky-50/50 p-3"
        data-testid="detail-view-yasal-bina"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 rounded-sm bg-sky-500" />
            <div className="text-[11px] uppercase tracking-widest font-bold text-sky-800">
              Yasal Bina Değerleri Toplamı
            </div>
          </div>
          <div className="text-base font-extrabold text-zinc-900">
            {yasalBinaToplam > 0 ? formatMoney(yasalBinaToplam) : "—"}
          </div>
        </div>
      </section>

      <section
        className="rounded-sm border border-amber-200 bg-amber-50/50 p-3"
        data-testid="detail-view-mevcut-bina"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 rounded-sm bg-amber-500" />
            <div className="text-[11px] uppercase tracking-widest font-bold text-amber-800">
              Mevcut Bina Değerleri Toplamı
            </div>
          </div>
          <div className="text-base font-extrabold text-zinc-900">
            {mevcutBinaToplam > 0 ? formatMoney(mevcutBinaToplam) : "—"}
          </div>
        </div>
      </section>

      <section className="rounded-sm border border-zinc-800 bg-zinc-900 p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-3 w-1 rounded-sm bg-white" />
          <div className="text-[11px] uppercase tracking-widest font-bold text-white">
            Toplam Değer (Otomatik)
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="rounded-sm bg-sky-500 border border-sky-400 p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-sky-100 mb-1.5">
              Yasal Toplam
            </div>
            <div className="text-2xl font-extrabold text-white tracking-tight leading-none">
              {yasalToplam > 0 ? yasalToplam.toLocaleString("tr-TR") : "0"}
              <span className="text-sm font-bold ml-1">₺</span>
            </div>
          </div>
          <div className="rounded-sm bg-amber-500 border border-amber-400 p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-900 mb-1.5">
              Mevcut Toplam
            </div>
            <div className="text-2xl font-extrabold text-zinc-900 tracking-tight leading-none">
              {mevcutToplam > 0 ? mevcutToplam.toLocaleString("tr-TR") : "0"}
              <span className="text-sm font-bold ml-1">₺</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
